import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingContact from '@/components/FloatingContact';
import SEOHead from '@/components/SEOHead';
import { trackFormSubmit } from '@/utils/analytics';

const ForArchitectsPage = () => {
  const { lang, getLocalized } = useLanguage();
  const [page, setPage] = useState(null);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', phone: '', type: 'Architect / Designer', message: '' });
  const [formStatus, setFormStatus] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, productsRes, settingsRes] = await Promise.all([
          api.get('/pages/for-architects'),
          api.get('/products'),
          api.get('/site-settings')
        ]);
        setPage(pageRes.data);
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Error:', err);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  const loc = page ? getLocalized(page) : {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    try {
      await api.post('/leads', { ...formData, locale: lang, page: 'for-architects' });
      setFormStatus('success');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', type: 'Architect / Designer', message: '' });
    } catch (err) {
      setFormStatus('error');
    }
  };

  return (
    <div data-testid="architects-page">
      <SEOHead
        title={lang === 'es' ? 'Para Arquitectos y Dise\u00f1adores' : 'For Architects & Designers'}
        description={lang === 'es' ? 'Sistemas dise\u00f1ados para su visi\u00f3n. Fichas t\u00e9cnicas, detalles de perfil y soluciones personalizadas para arquitectos.' : 'Systems designed for your vision. Technical data sheets, profile details, and custom solutions for architects.'}
        path="/for-architects"
      />
      <Navbar />
      <section className="bl-subpage-hero">
        <div className="bl-subpage-hero-bg" style={{ backgroundImage: `url(${loc.hero_image || ''})` }} />
        <div className="bl-subpage-hero-content">
          <div className="eyebrow">{lang === 'es' ? 'Para Profesionales' : 'For Professionals'}</div>
          <h1>{loc.title || 'For Architects'}</h1>
          <p>{loc.subtitle || ''}</p>
        </div>
      </section>

      <div className="bl-info-content">
        <p>{loc.content || ''}</p>
      </div>

      {loc.advantages && loc.advantages.length > 0 && (
        <div className="bl-advantages-grid">
          {loc.advantages.map((a, i) => (
            <div className="bl-advantage" key={i}>
              <h3>{a.title}</h3>
              <p>{a.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Architect-specific form */}
      <div style={{ padding: '6rem 5vw', background: 'var(--white)' }}>
        <div style={{ maxWidth: '600px' }}>
          <div className="bl-section-eyebrow">{lang === 'es' ? 'Especifica' : 'Specify'}</div>
          <div className="bl-section-title" style={{ marginBottom: '2rem' }}>{lang === 'es' ? 'Solicita Fichas T\u00e9cnicas' : 'Request Technical Sheets'}</div>
          {formStatus === 'success' ? (
            <div className="bl-form-success">
              {lang === 'es' ? 'Solicitud enviada. Te contactaremos pronto.' : 'Request sent! We\'ll be in touch soon.'}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="bl-form-row">
                <div className="bl-form-field">
                  <label>{lang === 'es' ? 'Nombre' : 'First Name'}</label>
                  <input type="text" value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required />
                </div>
                <div className="bl-form-field">
                  <label>{lang === 'es' ? 'Apellido' : 'Last Name'}</label>
                  <input type="text" value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required />
                </div>
              </div>
              <div className="bl-form-field">
                <label>Email</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required />
              </div>
              <div className="bl-form-field">
                <label>{lang === 'es' ? 'Tel\u00e9fono' : 'Phone'}</label>
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
              </div>
              <div className="bl-form-field">
                <label>{lang === 'es' ? 'Qu\u00e9 Necesitas' : 'What Do You Need'}</label>
                <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder={lang === 'es' ? 'Fichas t\u00e9cnicas, muestras, detalles de perfil...' : 'Technical sheets, samples, profile details...'} />
              </div>
              <button type="submit" className="bl-form-submit" disabled={formStatus === 'sending'}>
                {formStatus === 'sending' ? '...' : (lang === 'es' ? 'Enviar Solicitud \u2192' : 'Submit Request \u2192')}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer products={products} settings={settings} />
      <FloatingContact settings={settings} />
    </div>
  );
};

export default ForArchitectsPage;
